import {IconProps} from "@amzn/awsui-components-react-v3/polaris/icon";
import {
    isTerminalExecutionState,
    isUIBookingRequest,
    isBookingRequest,
    isUIAsnRequest,
    fetchLatestExecutionDetails,
    isShipmentExpedited,
    isCancelExecutionType,
    isCreateExecutionType,
    isBookingModificationExecutionState,
    isAsnUploadExecutionType,
    isExecutionOrderBookingRequest,
} from "../../util/ShippingRequestUtil";
import {AppConfig, AppConfigMapKey} from "./AppConfig";
import {getOrDefault} from "../../util/Utils";
import {
  IN_PROGRESS_AND_VALIDATION_SUCCESS_STATES,
  REQUEST_STATUS,
  SHIPPING_DETAILS
} from "../../util/ConfigConstants";
import {
    AWS_SCM_SHIPMENT_B_Y_BOOKING_CLASS,
    AWS_SCM_SHIPMENT_RACK_BOOKING,
    AWS_SCM_SHIPPING_REQUEST_CLASS
} from "./ShippingRequestClass";

export class ShippingRequestState {

    static readonly CREATED = new ShippingRequestState("Created", false, false, false, false, "status-info", "success", false, false, false);
    static readonly SUBMITTED = new ShippingRequestState("Submitted", false, false, false, false, "status-info", "success", false, false, false);
    static readonly TENTATIVE = new ShippingRequestState("Tentative", false, false, false, false, "status-pending", "subtle", false, false, false);
    static readonly SHIPMENT_PLANNED = new ShippingRequestState("ShipmentPlanned", false, false, false, true, "status-info", "success", true, false, true);
    static readonly LOAD_TENDERED = new ShippingRequestState("LoadTendered", false, false, false, true, "status-info", "success", true, false, false);
    static readonly LOAD_TENDER_CANCELLED = new ShippingRequestState("LoadTenderCancelled", false, false, false, false, "status-stopped", "subtle", false, false, false);
    static readonly BOOKING_REQUESTED = new ShippingRequestState("BookingRequested", false, false, false, true, "status-info", "success", true, true, false);
    static readonly BOOKING_ACCEPTED = new ShippingRequestState("BookingAccepted", false, false, false, true, "status-positive", "success", true, false, false);
    static readonly BOOKING_REJECTED = new ShippingRequestState("BookingRejected", false, false, false, true, "status-negative", "error", false, false, false);
    static readonly COMPLIANCE_HOLD = new ShippingRequestState("ComplianceHold", false, false, false, true, "status-pending", "subtle", true, false, false);
    static readonly PROCESSING = new ShippingRequestState("Processing", false, false, false, true, "status-in-progress", "success", false, false, false);
    static readonly ERROR = new ShippingRequestState("Error", false, false, false, false, "status-negative", "error", false, false, false);
    static readonly ON_HOLD = new ShippingRequestState("OnHold", false, false, false, true, "status-pending", "subtle", true, false, false);
    static readonly PENDING_ASSIGNMENT = new ShippingRequestState("PendingAssignment", false, false, false, true, "status-pending", "subtle", true, false, false);
    static readonly REDIRECTED = new ShippingRequestState("Redirected", false, false, false, true, "status-pending", "subtle", true, false, false);
    static readonly FAILURE = new ShippingRequestState("Failed", true, false, true, true, "status-negative", "error", false, false, false);
    static readonly ACCEPTED = new ShippingRequestState("Accepted", true, true, false, false, "status-positive", "success", false, false, false);
    static readonly CANCELLED = new ShippingRequestState("Cancelled", false, false, false, false, "status-stopped", "subtle", false, false, false);
    static readonly IN_PROGRESS = new ShippingRequestState("InProgress", false, false, false, true, "status-in-progress", "success", true, false, true);
    static readonly COMPLETED = new ShippingRequestState("Completed", false, false, false, false, "status-positive", "success", false, false, false);
    static readonly MANUALLY_COMPLETED = new ShippingRequestState("ManuallyCompleted", false, false, false, false, "status-positive", "success", false, false, false);

    static readonly ALL_STATES: ShippingRequestState[] = [
        ShippingRequestState.CREATED,
        ShippingRequestState.SUBMITTED,
        ShippingRequestState.TENTATIVE,
        ShippingRequestState.PROCESSING,
        ShippingRequestState.ERROR,
        ShippingRequestState.FAILURE,
        ShippingRequestState.ACCEPTED,
        ShippingRequestState.CANCELLED,
        ShippingRequestState.ON_HOLD,
        ShippingRequestState.PENDING_ASSIGNMENT,
        ShippingRequestState.REDIRECTED,
        ShippingRequestState.LOAD_TENDERED,
        ShippingRequestState.LOAD_TENDER_CANCELLED,
        ShippingRequestState.SHIPMENT_PLANNED,
        ShippingRequestState.BOOKING_REQUESTED,
        ShippingRequestState.BOOKING_ACCEPTED,
        ShippingRequestState.BOOKING_REJECTED,
        ShippingRequestState.COMPLIANCE_HOLD,
        ShippingRequestState.IN_PROGRESS,
        ShippingRequestState.COMPLETED,
        ShippingRequestState.MANUALLY_COMPLETED
    ]

    // private to restrict the creation other instances of this type
    /**
     *
     * @param name
     * @param isAsnEditable: Is asn shipping request editable if shipping request is in the concerned state
     * @param isAsnCancellable: Is asn shipping request cancellable if shipping request is in the concerned state
     * @param isBookingEditable: Is booking shipping request editable if shipping request is in the concerned state
     * @param isBookingCancellable: Is booking shipping request cancellable if shipping request is in the concerned state
     * @param iconName: used for determining which Icon should be displayed along with the Status of Booking.
     * @param iconVariant: used for determining which variant of the Icon should be displayed along with the Status of Booking.
     * @param isAsnAllowed: used for determining if asn over a booking is allowed.
     * @private
     */
    private constructor(public readonly name: string, private readonly isAsnEditable: boolean, private readonly isAsnCancellable: boolean,
        private readonly isBookingEditable: boolean, private readonly isBookingCancellable: boolean, public readonly iconName: IconProps.Name,
        public readonly iconVariant: IconProps.Variant, public readonly isAsnAllowed: boolean, public readonly isUpdateAllowed: boolean,
        public readonly isUpdateAllowedForLastMile: boolean) {
    }

    /*
        Edit is allowed on shipments if the following conditions are met:
        1. Edit feature should be enabled.
        2. Edit should be allowed on the current state of shipping request.
        3. Execution state is in correct state:
            a. For UI ASN request:
                i. The latest execution state is in terminal(COMPLETED/VALIDATION_ERROR) state.
            b. For Booking request:
                i. Either it should be a UI Booking request or update feature should be enabled.
                ii. We shouldn't have already received any legitimate cancel request on the shipment.
                iii. Either the latest execution state is in terminal(COMPLETED/VALIDATION_ERROR) state. Or if the latest execution type is
                    CREATE then execution state can be in modification(COMPLETED/IN_PROGRESS/VALIDATION_ERROR) state.
    */
    public isEditAllowed(shippingRequest: any) {
        if (AppConfig.getConfigValue(AppConfigMapKey.ENABLE_EDIT_FEATURE)) {
            const latestExecutionDetail = fetchLatestExecutionDetails(shippingRequest.executionDetails);

            let shouldAllowEditBooking = false;
            if (isBookingRequest(shippingRequest.properties)) {
                shouldAllowEditBooking = this.isEditAllowedForClass(shippingRequest.classId.id.value);
            }

            if (isUIAsnRequest(shippingRequest.properties)) {
                return this.isAsnEditable && latestExecutionDetail !== undefined && isTerminalExecutionState(latestExecutionDetail);
            } else if (shouldAllowEditBooking) {
              return latestExecutionDetail !== undefined && !isCancelExecutionType(latestExecutionDetail) &&
                (isTerminalExecutionState(latestExecutionDetail) ||
                  (isCreateExecutionType(latestExecutionDetail) && isBookingModificationExecutionState(latestExecutionDetail)));
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    public isEditAllowedForClass(shippingRequestClass: string | undefined) {
        // Using a new attribute in Shipping Request state isUpdateAllowed, so as to enable the MOT Update in a staged manner.
        // Once, it is completely enabled, we can remove this attribute.
        return AppConfig.getConfigValue(AppConfigMapKey.ENABLE_EDIT_FEATURE) && shippingRequestClass &&
          (this.isBookingEditable ||
            (((this.isUpdateAllowed && shippingRequestClass === AWS_SCM_SHIPMENT_B_Y_BOOKING_CLASS) ||
                (this.isUpdateAllowedForLastMile && shippingRequestClass === AWS_SCM_SHIPPING_REQUEST_CLASS)) &&
              AppConfig.getConfigValue(AppConfigMapKey.ENABLE_UPDATE_BOOKING)));
    }

    /*
        Expedite is allowed on shipments if the following conditions are met:
        1. Edit feature should be enabled.
        2. Shipping Request shouldn't be in failed state.
        3. Execution state is in correct state:
            a. For Booking request:
                i. We shouldn't have already received any legitimate cancel request on the shipment.
                ii. We shouldn't have already received any legitimate expedite request on the shipment.
                iii. Either the latest execution state is in terminal(COMPLETED/VALIDATION_ERROR) state. Or if the latest execution type is
                    CREATE/ASN_UPLOAD then execution state can be in modification(COMPLETED/IN_PROGRESS/VALIDATION_ERROR) state.
    */
    public isExpediteAllowed(shippingRequest: any) {
        if (AppConfig.getConfigValue(AppConfigMapKey.ENABLE_EDIT_FEATURE)) {
            const latestExecutionDetail = fetchLatestExecutionDetails(shippingRequest.executionDetails);
            if ((isBookingRequest(shippingRequest.properties) && shippingRequest.classId?.id?.value === AWS_SCM_SHIPMENT_B_Y_BOOKING_CLASS) ||
                (isExecutionOrderBookingRequest(shippingRequest.properties) && shippingRequest.classId?.id?.value === AWS_SCM_SHIPMENT_RACK_BOOKING)) {
                if (shippingRequest.classId?.id?.value === AWS_SCM_SHIPMENT_RACK_BOOKING &&
                    shippingRequest.routeType === SHIPPING_DETAILS.ROUTE_TYPE_EXPRESS) {
                    return false;
                }
                return this !== ShippingRequestState.FAILURE && this !== ShippingRequestState.ON_HOLD
                    && this !== ShippingRequestState.COMPLIANCE_HOLD && this !== ShippingRequestState.BOOKING_REJECTED && this !== ShippingRequestState.ERROR
                    && latestExecutionDetail !== undefined &&
                    !isCancelExecutionType(latestExecutionDetail) && !isShipmentExpedited(shippingRequest.executionDetails) &&
                    (isTerminalExecutionState(latestExecutionDetail) ||
                    ((isCreateExecutionType(latestExecutionDetail) || isAsnUploadExecutionType(latestExecutionDetail)) &&
                    (isBookingModificationExecutionState(latestExecutionDetail) || shippingRequest.classId?.id?.value === AWS_SCM_SHIPMENT_RACK_BOOKING)));
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    /*
        Cancel is allowed on shipments if the following conditions are met:
        1. Cancel feature should be enabled.
        2. Cancel should be allowed on the current state of shipping request.
        3. Execution state is in correct state:
            a. For UI ASN request:
                i. The latest execution state is in terminal(COMPLETED/VALIDATION_ERROR) state.
            b. For UI Booking request:
                i. We shouldn't have already received any legitimate cancel request on the shipment.
                ii. The latest execution state is in modification(COMPLETED/IN_PROGRESS/VALIDATION_ERROR) state.
    */
    public isCancelAllowed(shippingRequest: any) {
        if (AppConfig.getConfigValue(AppConfigMapKey.ENABLE_CANCEL_FEATURE)) {
            const latestExecutionDetail = fetchLatestExecutionDetails(shippingRequest.executionDetails);
            if (isUIAsnRequest(shippingRequest.properties)) {
                return this.isAsnCancellable && latestExecutionDetail !== undefined && isTerminalExecutionState(latestExecutionDetail);
            } else if (isBookingRequest(shippingRequest.properties)) {
                return this.isBookingCancellable && latestExecutionDetail !== undefined &&
                    !isCancelExecutionType(latestExecutionDetail) && isBookingModificationExecutionState(latestExecutionDetail);
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    /*
    Asn Upload is allowed on shipments if the following conditions are met:
    1. Asn Upload feature should be enabled in the cell.
    2. Shipment is a Booking (UI-created or EDI-created).
    3. Asn Upload should be enabled for the state i.e. Shipping Request shouldn't be in failed/Processing/Submitted/Cancelled/Error state.
    4. Last Asn Upload should be in terminal state.
    5. Latest Execution is in correct state:
      i. We shouldn't have already received any legitimate cancel request on the shipment.
      ii. Either the latest execution type is CREATE then execution state can be in modification(COMPLETED/IN_PROGRESS/VALIDATION_ERROR) state
           or if the latest execution state is in terminal(COMPLETED/VALIDATION_ERROR) state.
*/
    public isAsnUploadAllowed(shippingRequest: any) {
        if (AppConfig.getConfigValue(AppConfigMapKey.ENABLE_ASN_OVER_BOOKING_FEATURE)) {
          const latestAsnUploadExecutionDetail = fetchLatestExecutionDetails(shippingRequest.executionDetails, undefined, [SHIPPING_DETAILS.EXECUTION_DETAIL_TYPE_ASN_UPLOAD]);
          const latestExecutionDetail = fetchLatestExecutionDetails(shippingRequest.executionDetails);
            return (isUIBookingRequest(shippingRequest.properties) ||
                    (isBookingRequest(shippingRequest.properties) && AppConfig.getConfigValue(AppConfigMapKey.ENABLE_UI_ASN_OVER_EDI_BOOKING)))
                && this.isAsnAllowed
              && (latestAsnUploadExecutionDetail === undefined || !IN_PROGRESS_AND_VALIDATION_SUCCESS_STATES.includes(latestAsnUploadExecutionDetail?.executionState))
              && latestExecutionDetail !== undefined
              && !isCancelExecutionType(latestExecutionDetail)
              && ((isCreateExecutionType(latestExecutionDetail) && isBookingModificationExecutionState(latestExecutionDetail)) || isTerminalExecutionState(latestExecutionDetail));
        } else {
            return false;
        }
    }

    public static getShippingRequestState(name: string) {
        return ShippingRequestState.ALL_STATES.find(value => value.name === name);
    }

    public static toHumanReadable(name: string) {
        return getOrDefault(name, REQUEST_STATUS);
    }

    public static fetchAllEditableStates(isBookingRequest: boolean) {
        return isBookingRequest ? ShippingRequestState.ALL_STATES.filter(value => value.isBookingEditable).map(value => value.name) :
            ShippingRequestState.ALL_STATES.filter(value => value.isAsnEditable).map(value => value.name);
    }
}